import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridStack } from 'gridstack'
import 'gridstack/dist/gridstack.min.css'
import WidgetContainer from './WidgetContainer'
import WidgetListDialog from './WidgetListDialog'
import getRandomUUID from '../../../generic/utilities/randomuuid/RandomUUID'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import WidgetEditionToolbar from './WidgetEditionToolbar'
import { FOOTER_HEIGHT } from '../../../generic/utilities/size_utils'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
import { apiGet } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import { getWidgetProviderForType } from './WidgetProvider'
import { Button, Stack, Typography } from '@mui/material'
import SensitiveBoundary from '../../../vision/SensitiveBoundary'

const WidgetGrid = ({ widgets, setWidgets, reloadWidgets, onSave, editing, setEditing, onTemplatesClick, showAllWidgets = false, preview = false, toolbarLabel = null }) => {
  const refs = useRef({})
  const gridRef = useRef(null)
  const gridInstance = useRef(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const { t_ } = useSmartTranslation()
  const [fabricationOrder, setFabricationOrder] = useState(null)
  const [jobReference, setJobReference] = useState(null)
  const [worker, setWorker] = useState(null)
  const isUserAuthorized = useAuthorization()
  const [showGrid, setShowGrid] = useState(true)
  const columns = 12
  const cellHeight = 80
  const borderSize = '1px'
  const borderColor = 'white'
  const { device, globalData, navigateToDeviceId, enableFullscreen } = useMosInfo()
  const changeBufferRef = useRef({})
  const debounceTimerRef = useRef(null)
  const changeDebounceMs = 100
  const saveRequestedRef = useRef(false)

  const applyBufferedChangesNow = useCallback(() => {
    const buffered = changeBufferRef.current
    const ids = Object.keys(buffered)
    if (ids.length === 0) {
      return false
    }

    changeBufferRef.current = {}
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    setWidgets((oldWidgets) => {
      return oldWidgets.map((widget) => {
        const item = buffered[widget.id]
        if (!item) return widget
        return {
          ...widget,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          minW: item.minW,
          minH: item.minH
        }
      })
    })
    return true
  }, [setWidgets])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Make sure that the worker is logged
  useEffect(() => {
    if (!worker || !device) {
      if (!device) setWorker(null)
      return
    }

    let cancelled = false
    let timeoutId

    const checkWorker = async () => {
      try {
        const workers = await apiGet('mos/presence/device/' + device.id + '/workers')

        if (!cancelled && !workers.find(w => w.id === worker.id)) {
          setWorker(null)
          return
        }
      } catch (err) {
        console.error(err)
      }

      if (!cancelled) {
        timeoutId = setTimeout(checkWorker, 1000)
      }
    }

    checkWorker()

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [device, worker])

  // Create refs for each widget
  widgets.forEach(({ id }) => {
    if (!refs.current[id]) refs.current[id] = createRef()
  }, [])

  // Initialize GridStack once
  useEffect(() => {
    if (!gridRef.current || gridInstance.current) return

    const grid = GridStack.init({
      cellHeight,
      margin: 0,
      staticGrid: false,
      float: true,
      column: columns,
      disableOneColumnMode: true
    }, gridRef.current)
    gridInstance.current = grid

    // When items move or resize, sync them with React state
    grid.on('change', (event, changedItems) => {
      changedItems.forEach((item) => {
        changeBufferRef.current[item.el.dataset.id] = item
      })
      if (!debounceTimerRef.current) {
        debounceTimerRef.current = setTimeout(() => {
          const buffered = changeBufferRef.current
          changeBufferRef.current = {}
          debounceTimerRef.current = null
          setWidgets((oldWidgets) => {
            return oldWidgets.map((widget) => {
              if (widget.id in buffered) {
                const item = buffered[widget.id]
                return {
                  ...widget,
                  x: item.x,
                  y: item.y,
                  w: item.w,
                  h: item.h,
                  minW: item.minW,
                  minH: item.minH
                }
              }
              return widget
            })
          })
        }, changeDebounceMs)
      }
    })
  }, [setWidgets])

  // Toggle edit mode
  useEffect(() => {
    if (!gridInstance.current) {
      return
    }
    gridInstance.current.setStatic(!editing)
  }, [editing])

  // After widgets render or change, activate any new items
  useEffect(() => {
    if (!gridInstance.current) return
    widgets.forEach((item) => {
      const el = refs.current[item.id]?.current
      if (el && !el.gridstackNode) {
        const providerInfo = getWidgetProviderForType(item.type)?.getWidgetInfo?.() || {}
        const shouldAutoPosition = item.x === undefined || item.x === null || item.y === undefined || item.y === null
        const widgetOpts = {
          w: item.w,
          h: item.h,
          minW: providerInfo.minW ?? item.minW,
          minH: providerInfo.minH ?? item.minH,
          maxW: providerInfo.maxW,
          maxH: providerInfo.maxH,
          autoPosition: shouldAutoPosition
        }
        if (!shouldAutoPosition) {
          widgetOpts.x = item.x
          widgetOpts.y = item.y
        }
        gridInstance.current.makeWidget(el, widgetOpts)
      }
    })
  }, [widgets])

  const syncAllNodesToState = useCallback(() => {
    const grid = gridInstance.current
    if (!grid) return false
    const nodes = grid.engine?.nodes || []
    const idToNode = new Map()
    nodes.forEach((n) => {
      const id = n?.el?.dataset?.id
      if (id) idToNode.set(String(id), n)
    })
    let changed = false
    setWidgets((old) => old.map((w) => {
      const node = idToNode.get(String(w.id))
      if (!node) return w
      if (w.x !== node.x || w.y !== node.y || w.w !== node.w || w.h !== node.h ||
        w.minW !== node.minW || w.minH !== node.minH) {
        changed = true
        return {
          ...w,
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          minW: node.minW,
          minH: node.minH
        }
      }
      return w
    }))
    return changed
  }, [setWidgets])

  const handleSaveClick = useCallback(() => {
    const flushed = applyBufferedChangesNow()
    const synced = syncAllNodesToState()
    if (flushed || synced) {
      saveRequestedRef.current = true
      return
    }
    onSave()
  }, [applyBufferedChangesNow, syncAllNodesToState, onSave])

  useEffect(() => {
    if (!saveRequestedRef.current) return
    if (debounceTimerRef.current) return
    if (Object.keys(changeBufferRef.current).length > 0) return
    saveRequestedRef.current = false
    onSave()
  }, [widgets, onSave])

  const handleAdd = useCallback((widgetProvider) => {
    const nextId = getRandomUUID()
    const widgetInfo = widgetProvider.getWidgetInfo()

    const newWidget = {
      id: nextId,
      w: widgetInfo.defaultW,
      h: widgetInfo.defaultH,
      minW: widgetInfo.minW,
      minH: widgetInfo.minH,
      type: widgetProvider.getCode(),
      data: widgetProvider.getDefaultConfiguration()
    }
    setWidgets((oldWidgets) => ([...oldWidgets, newWidget]))
  }, [setWidgets])

  const handleCompact = useCallback(() => {
    if (gridInstance.current) {
      gridInstance.current.compact()
    }
  }, [])

  const handleRemove = useCallback((id) => {
    const grid = gridInstance.current
    const el = gridRef.current.querySelector(`[data-id='${id}']`)
    if (grid && el) {
      grid.removeWidget(el, false)
    }

    setWidgets((oldWidgets) => {
      return oldWidgets.filter((item) => item.id !== id)
    })
  }, [setWidgets])

  const setFabricationOrderAndJobReference = useCallback((newFo, newJobRef) => {
    setFabricationOrder(newFo)
    setJobReference(newJobRef)
  }, [])

  const generatedWidgets = useMemo(() => {
    if (!widgets) {
      return null
    }

    return widgets.map((widgetData) => {
      const props = { ...widgetData }
      props.columns = columns
      props.cellHeight = cellHeight
      props.fabricationOrder = fabricationOrder
      props.jobReference = jobReference
      props.setFabricationOrderAndJobReference = setFabricationOrderAndJobReference
      props.worker = worker
      props.setWorker = setWorker
      props.device = device
      props.navigateToDeviceId = navigateToDeviceId
      props.enableFullscreen = enableFullscreen

      const widgetId = widgetData.id
      const widgetProvider = getWidgetProviderForType(widgetData.type)

      const setData = (data) => {
        setWidgets((oldWidgets) => {
          return oldWidgets.map((item) => {
            if (item.id === widgetData.id) {
              return { ...item, data }
            }
            return item
          })
        })
      }

      props.setData = setData
      props.setDate = setData

      const widgetProviderInfo = widgetProvider.getWidgetInfo()

      const headerContent = widgetData.invalidConfig
        ? (
          <Stack direction='row' gap={1} alignItems='center'>
            <Typography color='error'>{t_('Invalid configuration')}</Typography>
            {isUserAuthorized('MOS_EDIT')
              ? (
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => {
                    const defaultConf = widgetProvider.getDefaultConfiguration ? widgetProvider.getDefaultConfiguration() : null
                    setWidgets((oldWidgets) => {
                      return oldWidgets.map((item) => {
                        if (item.id === widgetId) {
                          return { ...item, data: defaultConf, invalidConfig: false }
                        }
                        return item
                      })
                    })
                  }}
                >
                  {t_('Reset')}
                </Button>
                )
              : null}
          </Stack>
          )
        : widgetProvider.getCustomHeader(props)

      return (
        <WidgetContainer
          key={widgetId}
          widgetId={widgetId}
          ref={refs.current[widgetId]}
          editable={editing}
          renderSettings={widgetProvider.createConfigurationWidget(widgetData.data, setData, t_)}
          onRemove={() => handleRemove(widgetId)}
          x={widgetData.x}
          y={widgetData.y}
          w={widgetData.w}
          h={widgetData.h}
          minW={widgetProviderInfo.minW}
          minH={widgetProviderInfo.minH}
          title={widgetProvider.getName(globalData, widgetData.data)}
          icon={widgetProvider.getIcon(globalData)}
          preview={false}
          widgetProviderInfo={widgetProviderInfo}
          status={widgetProvider.getStatus(globalData)}
          header={headerContent}
          onClick={() => { if (!editing && !preview) { widgetProvider.onClick(props) } }}
          isClickable={!preview && widgetProvider.isClickable()}
        >
          <SensitiveBoundary sensitive={widgetData.sensitive === true}>
            {widgetProvider.createWidget(props)}
          </SensitiveBoundary>
        </WidgetContainer>
      )
    }

    )
  }, [widgets, editing, preview, handleRemove, t_, setWidgets, fabricationOrder, jobReference, worker, setFabricationOrderAndJobReference, globalData, device, enableFullscreen, navigateToDeviceId, isUserAuthorized])

  const gridStyle = editing && showGrid && generatedWidgets && generatedWidgets.length > 0
    ? {
        backgroundImage:
        'linear-gradient(to right, ' + borderColor + ' ' + borderSize + ', transparent ' + borderSize + '), ' +
        'linear-gradient(to bottom, ' + borderColor + ' ' + borderSize + ', transparent ' + borderSize + ')',
        backgroundSize: `${100 / columns}% ${cellHeight}px`,
        borderBottom: borderSize + ' solid ' + borderColor,
        borderRight: borderSize + ' solid ' + borderColor
      }
    : {}

  return (
    <>
      <div style={{ height: '100%' }}>
        {isUserAuthorized('MOS_EDIT') && !preview
          ? (
            <WidgetListDialog
              handleAdd={handleAdd}
              open={addDialogOpen}
              setOpen={setAddDialogOpen}
              columns={columns}
              cellHeight={cellHeight}
              showAll={showAllWidgets}
            />)
          : null}
        <div
          ref={gridRef}
          className='grid-stack controlled' // IMPORTANT: needed for gridstack to work
          style={{ ...gridStyle }}
        >
          {generatedWidgets}
        </div>
      </div>
      {isUserAuthorized('MOS_EDIT') && !preview
        ? (
          <div style={{ pointerEvents: 'none', position: 'sticky', left: '1rem', right: '1rem', bottom: 'calc(1rem + ' + FOOTER_HEIGHT + ')', paddingTop: '1rem' }}>
            <WidgetEditionToolbar
              editing={editing}
              setEditing={setEditing}
              onAddClick={() => setAddDialogOpen(true)}
              onRevert={() => {
                gridInstance.current.removeAll(false)
                setEditing(false)
                reloadWidgets()
              }}
              onSave={handleSaveClick}
              showGrid={showGrid}
              setShowGrid={setShowGrid}
              onCompact={handleCompact}
              onTemplatesClick={onTemplatesClick}
              deviceName={toolbarLabel || device?.name || null}
            />
          </div>
          )
        : null}
    </>
  )
}

export default WidgetGrid
